import React from 'react';
import { Crosshair, Target, Zap, BarChart3, TrendingUp, Share2, Globe, Megaphone, Video, Mail, Palette, Code, Search, Users, Building2, ShoppingBag, Heart, Factory, GraduationCap, Landmark, Stethoscope, Plane, Car, Utensils } from 'lucide-react';

// ============================================================
// MEGA MENU NAVIGATION STRUCTURE
// ============================================================

export interface MegaMenuItem {
  name: string;
  href: string;
  description?: string;
  icon?: React.ReactNode;
}

export interface MegaMenuColumn {
  title: string;
  items: MegaMenuItem[];
  featured?: {
    title: string;
    description: string;
    href: string;
    cta: string;
  };
}

export interface NavItem {
  name: string;
  href: string;
  type: 'page' | 'anchor' | 'mega';
  megaMenu?: MegaMenuColumn[];
}

export const NAVIGATION: NavItem[] = [
  {
    name: 'SERVICES',
    href: '/services',
    type: 'mega',
    megaMenu: [
      {
        title: 'PERFORMANCE',
        items: [
          { name: 'Enterprise PPC', href: '/services/ppc', description: 'High-budget Google Ads, Meta & LinkedIn campaigns', icon: React.createElement(Crosshair, { className: "w-5 h-5" }) },
          { name: 'SEO Warfare', href: '/services/seo', description: 'Dominate search for high-value keywords', icon: React.createElement(Target, { className: "w-5 h-5" }) },
          { name: 'Performance Marketing', href: '/services/performance-marketing', description: 'Full-funnel ROI-driven advertising', icon: React.createElement(TrendingUp, { className: "w-5 h-5" }) },
          { name: 'Performance Analytics', href: '/services/analytics', description: 'Enterprise attribution & reporting', icon: React.createElement(BarChart3, { className: "w-5 h-5" }) },
        ],
      },
      {
        title: 'GROWTH',
        items: [
          { name: 'Social Media Marketing', href: '/services/social-media', description: 'Viral content strategies for enterprise brands', icon: React.createElement(Share2, { className: "w-5 h-5" }) },
          { name: 'Conversion Optimization', href: '/services/conversion-optimization', description: 'Transform traffic into high-ticket customers', icon: React.createElement(Zap, { className: "w-5 h-5" }) },
          { name: 'Video & Creative', href: '/services/video-creative', description: 'Cinematic brand storytelling & ad creative', icon: React.createElement(Video, { className: "w-5 h-5" }) },
          { name: 'Email & Automation', href: '/services/email-automation', description: 'Nurture sequences & lifecycle marketing', icon: React.createElement(Mail, { className: "w-5 h-5" }) },
        ],
      },
      {
        title: 'SPECIALIZED',
        items: [
          { name: 'Brand Strategy', href: '/services/brand-strategy', description: 'Positioning, messaging & market dominance', icon: React.createElement(Megaphone, { className: "w-5 h-5" }) },
          { name: 'Web Development', href: '/services/web-development', description: 'High-performance websites & landing pages', icon: React.createElement(Code, { className: "w-5 h-5" }) },
          { name: 'Marketplace Marketing', href: '/services/marketplace', description: 'Amazon, Flipkart & e-commerce growth', icon: React.createElement(Globe, { className: "w-5 h-5" }) },
        ],
        featured: {
          title: 'Not sure what you need?',
          description: 'Book a free 30-minute strategy session. We\'ll audit your current marketing and identify the highest-impact opportunities.',
          href: '/contact',
          cta: 'Book Strategy Session →',
        },
      },
    ],
  },
  {
    name: 'RESULTS',
    href: '/results',
    type: 'mega',
    megaMenu: [
      {
        title: 'PROOF',
        items: [
          { name: 'Case Studies', href: '/results/case-studies', description: 'Real numbers from real campaigns' },
          { name: 'Client Testimonials', href: '/results/testimonials', description: 'What our clients say about working with us' },
          { name: 'Industry Results', href: '/results/industries', description: 'Results broken down by vertical' },
        ],
      },
      {
        title: 'METRICS',
        items: [
          { name: '50+ Enterprises Served', href: '/results/case-studies', description: 'Across India, UAE & KSA' },
          { name: '₹142Cr+ Revenue Generated', href: '/results/case-studies', description: 'For our clients in 2025-26' },
          { name: '12x Average ROAS', href: '/results/case-studies', description: 'Return on ad spend' },
          { name: '94% Client Retention', href: '/results/testimonials', description: 'Year-over-year' },
        ],
      },
      {
        title: 'INDUSTRIES',
        items: [
          { name: 'Technology & SaaS', href: '/results/industries/technology' },
          { name: 'Real Estate', href: '/results/industries/real-estate' },
          { name: 'Healthcare', href: '/results/industries/healthcare' },
          { name: 'E-commerce & D2C', href: '/results/industries/ecommerce' },
          { name: 'Finance & Banking', href: '/results/industries/finance' },
          { name: 'Education', href: '/results/industries/education' },
        ],
      },
    ],
  },
  {
    name: 'TOOLS',
    href: '/tools',
    type: 'mega',
    megaMenu: [
      {
        title: 'FREE TOOLS',
        items: [
          { name: 'ROI Calculator', href: '/tools/roi-calculator', description: 'Calculate your marketing ROI instantly' },
          { name: 'ROAS Calculator', href: '/tools/roas-calculator', description: 'Measure return on ad spend' },
          { name: 'Ad Spend Estimator', href: '/tools/ad-spend-estimator', description: 'Benchmark your ad spend by industry' },
        ],
      },
    ],
  },
  {
    name: 'ABOUT',
    href: '/about',
    type: 'mega',
    megaMenu: [
      {
        title: 'COMPANY',
        items: [
          { name: 'Our Story', href: '/about', description: 'Why we built DisruptNext' },
          { name: 'E3 Methodology', href: '/about/e3-methodology', description: 'Embedded. Essential. Engineered.' },
          { name: 'How We Work', href: '/about/process', description: 'From first call to revenue growth' },
          { name: 'Careers', href: '/about/careers', description: 'Join the disruption' },
        ],
      },
      {
        title: 'RESOURCES',
        items: [
          { name: 'Manifesto', href: '/manifesto', description: 'Our philosophy on marketing' },
          { name: 'Blog', href: '/blog', description: 'Insights & strategies' },
        ],
      },
    ],
  },
  {
    name: 'LOCATIONS',
    href: '/locations',
    type: 'mega',
    megaMenu: [
      {
        title: 'INDIA',
        items: [
          { name: 'Kochi', href: '/kochi' },
          { name: 'Mumbai', href: '/mumbai' },
          { name: 'Delhi NCR', href: '/delhi' },
          { name: 'Bangalore', href: '/bangalore' },
          { name: 'Chennai', href: '/chennai' },
          { name: 'Hyderabad', href: '/hyderabad' },
          { name: 'Pune', href: '/pune' },
          { name: 'Kolkata', href: '/kolkata' },
        ],
      },
      {
        title: 'MIDDLE EAST',
        items: [
          { name: 'Dubai', href: '/dubai' },
          { name: 'Abu Dhabi', href: '/abu-dhabi' },
          { name: 'Riyadh', href: '/riyadh' },
          { name: 'Jeddah', href: '/jeddah' },
          { name: 'Dammam', href: '/dammam' },
        ],
      },
      {
        title: 'GLOBAL',
        items: [
          { name: 'New York', href: '/new-york' },
          { name: 'London', href: '/london' },
          { name: 'Singapore', href: '/singapore' },
          { name: 'All Locations', href: '/locations' },
        ],
      },
    ],
  },
];

// Legacy flat nav for mobile fallback
export const NAVIGATION_LINKS = NAVIGATION.map(item => ({
  name: item.name,
  href: item.href,
  type: item.type === 'mega' ? 'page' as const : item.type,
}));

// ============================================================
// SERVICES DATA (for home page & service pages)
// ============================================================

export const SERVICES = [
  {
    id: '01',
    title: 'Enterprise PPC',
    slug: 'ppc',
    description: 'High-budget Google Ads, Meta, and LinkedIn campaigns engineered for enterprise. We target high-value keywords and optimize for revenue, not vanity clicks.',
    icon: React.createElement(Crosshair, { className: "w-8 h-8 text-brand-red" }),
  },
  {
    id: '02',
    title: 'SEO Warfare',
    slug: 'seo',
    description: 'Enterprise SEO that dominates search for high-intent keywords. Technical audits, content strategy, and authority building that outranks competitors.',
    icon: React.createElement(Target, { className: "w-8 h-8 text-brand-red" }),
  },
  {
    id: '03',
    title: 'Conversion Optimization',
    slug: 'conversion-optimization',
    description: 'Data-driven CRO for enterprise funnels. Landing page optimization, A/B testing, and conversion tracking that turns traffic into revenue.',
    icon: React.createElement(Zap, { className: "w-8 h-8 text-brand-red" }),
  },
  {
    id: '04',
    title: 'Performance Analytics',
    slug: 'analytics',
    description: 'Enterprise-grade attribution, dashboards, and reporting. Every rupee tracked to revenue with multi-touch attribution and CRM integration.',
    icon: React.createElement(BarChart3, { className: "w-8 h-8 text-brand-red" }),
  },
];

// ============================================================
// METRICS (for KillCount / hero)
// ============================================================

export const METRICS = [
  { label: 'Enterprises Served', value: '50', prefix: '', suffix: '+' },
  { label: 'Revenue Generated', value: '142', prefix: '₹', suffix: 'Cr+' },
  { label: 'Average ROAS', value: '12', prefix: '', suffix: 'x' },
  { label: 'Client Retention', value: '94', prefix: '', suffix: '%' },
];

// ============================================================
// INDUSTRIES
// ============================================================

export const INDUSTRIES = [
  { name: 'Technology & SaaS', icon: React.createElement(Code, { className: "w-6 h-6" }), href: '/results/industries/technology' },
  { name: 'Real Estate', icon: React.createElement(Building2, { className: "w-6 h-6" }), href: '/results/industries/real-estate' },
  { name: 'E-commerce & D2C', icon: React.createElement(ShoppingBag, { className: "w-6 h-6" }), href: '/results/industries/ecommerce' },
  { name: 'Healthcare', icon: React.createElement(Heart, { className: "w-6 h-6" }), href: '/results/industries/healthcare' },
  { name: 'Finance & Banking', icon: React.createElement(Landmark, { className: "w-6 h-6" }), href: '/results/industries/finance' },
  { name: 'Education', icon: React.createElement(GraduationCap, { className: "w-6 h-6" }), href: '/results/industries/education' },
  { name: 'Manufacturing', icon: React.createElement(Factory, { className: "w-6 h-6" }), href: '/results/industries/manufacturing' },
  { name: 'Travel & Hospitality', icon: React.createElement(Plane, { className: "w-6 h-6" }), href: '/results/industries/travel' },
];

// ============================================================
// CASE STUDIES
// ============================================================

export const CASE_STUDIES = [
  {
    id: 'cs-01',
    client: 'Enterprise SaaS Company',
    industry: 'Technology',
    metric: '+325%',
    metricLabel: 'Increase in Qualified Leads',
    description: 'Transformed their PPC strategy from broad-match chaos to precision-targeted campaigns. Revenue up 4x in 6 months.',
    services: ['PPC', 'SEO', 'Analytics'],
    duration: '6 months',
    roas: '18x',
  },
  {
    id: 'cs-02',
    client: 'Luxury Real Estate Developer',
    industry: 'Real Estate',
    metric: '₹47Cr',
    metricLabel: 'Revenue Generated',
    description: 'Full-funnel marketing for a premium real estate launch. From awareness to site visits to bookings — every touchpoint engineered.',
    services: ['PPC', 'Social', 'CRO'],
    duration: '8 months',
    roas: '22x',
  },
  {
    id: 'cs-03',
    client: 'D2C Health Brand',
    industry: 'Healthcare',
    metric: '+650%',
    metricLabel: 'ROAS Improvement',
    description: 'Rebuilt their entire digital presence. New landing pages, retargeting funnels, and email automation. Cost per acquisition dropped 70%.',
    services: ['CRO', 'Email', 'Analytics'],
    duration: '4 months',
    roas: '15x',
  },
  {
    id: 'cs-04',
    client: 'National E-commerce Brand',
    industry: 'E-commerce',
    metric: '₹112Cr',
    metricLabel: 'Annual Revenue',
    description: 'Scaled from ₹18Cr to ₹112Cr annual revenue through aggressive performance marketing and marketplace optimization.',
    services: ['PPC', 'SEO', 'Marketplace'],
    duration: '12 months',
    roas: '12x',
  },
];
